console.log('🔍 API_BASE:', import.meta.env.VITE_API_BASE_URL);

// src/api/neogtmClient.js
import { apiFetch } from "@/utils/apiFetch";
import { normalizeTenantId } from "@/lib/tenantUtils";

// Helper: build querystring from an object
const qs = (obj = {}) => {
  const params = new URLSearchParams();
  Object.entries(obj).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    if (typeof v === 'object') {
      params.append(k, JSON.stringify(v));
    } else {
      params.append(k, String(v));
    }
  });
  const s = params.toString();
  return s ? `?${s}` : "";
};

// Helper: get effective tenant ID - only use admin-active-tenant when user is admin
const getEffectiveTenantId = () => {
  const isAdmin = localStorage.getItem("user-role") === "admin";
  const adminTenant = isAdmin ? localStorage.getItem("admin-active-tenant") : null;
  const runtimeSessionTenant = typeof window !== "undefined" ? window.__NEO_SESSION_TENANT_ID : null;
  const raw =
    adminTenant ||
    runtimeSessionTenant ||
    localStorage.getItem("active-tenant") ||
    localStorage.getItem("user-tenant-id") ||
    null;
  return normalizeTenantId(raw) || null;
};

// Helper: get headers with tenant ID and user ID
const getHeaders = (extra = {}) => {
  const tenantId = getEffectiveTenantId();
  const userId = localStorage.getItem('user-id');
  const headers = { ...extra };
  if (tenantId) headers["X-Tenant-ID"] = tenantId;
  if (userId) headers["X-User-ID"] = userId;
  return headers;
};

// Generic entity client that calls your API
function makeEntity(entityName) {
  const base = `/api/entities/${encodeURIComponent(entityName)}`;

  return {
    // Base44-compatible filter(where, orderBy, limit)
    filter: async (where = {}, orderBy = "", limit = 100) => {
      const params = { where: JSON.stringify(where) };
      if (orderBy) params.order = orderBy;
      if (limit) params.limit = limit;
      
      const r = await apiFetch(`${base}${qs(params)}`, { 
        credentials: "include",
        headers: getHeaders()
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        const err = new Error(data.message || data.error || `${entityName}.filter failed (${r.status})`);
        err.status = r.status;
        err.body = data;
        throw err;
      }
      return r.json();
    },

    list: async (params = {}) => {
      const r = await apiFetch(`${base}${qs(params)}`, { 
        credentials: "include",
        headers: getHeaders()
      });
      if (!r.ok) throw new Error(`${entityName}.list failed (${r.status})`);
      return r.json();
    },

    get: async (id, tenantId) => {
      const url = tenantId 
        ? `${base}/${encodeURIComponent(id)}?tenant_id=${encodeURIComponent(tenantId)}`
        : `${base}/${encodeURIComponent(id)}`;
      const r = await apiFetch(url, { 
        credentials: "include",
        headers: getHeaders()
      });
      if (!r.ok) throw new Error(`${entityName}.get failed (${r.status})`);
      return r.json();
    },

    create: async (record) => {
      const r = await apiFetch(base, {
        method: "POST",
        headers: getHeaders({ "Content-Type": "application/json" }),
        credentials: "include",
        body: JSON.stringify(record),
      });
      if (!r.ok) {
        const err = new Error(`${entityName}.create failed (${r.status})`);
        err.status = r.status;
        throw err;
      }
      return r.json();
    },

    update: async (id, patch) => {
      const r = await apiFetch(`${base}/${encodeURIComponent(id)}`, {
        method: "PATCH",
        headers: getHeaders({ "Content-Type": "application/json" }),
        credentials: "include",
        body: JSON.stringify(patch),
      });
      if (!r.ok) {
        const err = new Error(`${entityName}.update failed (${r.status})`);
        err.status = r.status;
        throw err;
      }
      return r.json();
    },

    delete: async (id) => {
      const r = await apiFetch(`${base}/${encodeURIComponent(id)}`, {
        method: "DELETE",
        credentials: "include",
        headers: getHeaders()
      });
      if (!r.ok) {
        const err = new Error(`${entityName}.delete failed (${r.status})`);
        err.status = r.status;
        throw err;
      }
      return r.json().catch(() => ({}));
    },

    bulkCreate: async (records) => {
      const r = await apiFetch(`${base}/bulkCreate`, {
        method: "POST",
        headers: getHeaders({ "Content-Type": "application/json" }),
        credentials: "include",
        body: JSON.stringify({ records }),
      });
      if (!r.ok) throw new Error(`${entityName}.bulkCreate failed (${r.status})`);
      return r.json();
    },
  };
}

export const neogtm = {
  entities: new Proxy(
    {},
    {
      get: (_, prop) => makeEntity(String(prop)),
    }
  ),

  auth: {
    me: async () => {
      const r = await apiFetch("/api/user/me", { 
        credentials: "include",
        headers: getHeaders()
      });
      if (!r.ok) {
        const err = new Error(r.status === 403 ? "Access denied" : "Not authenticated");
        err.status = r.status;
        throw err;
      }
      const user = await r.json();
      // Store user ID for subsequent requests
      if (user.id) localStorage.setItem('user-id', user.id);
      if (user.tenant_id) localStorage.setItem('user-tenant-id', user.tenant_id);
      return user;
    },

    logout: async () => {
      localStorage.removeItem('user-id');
      localStorage.removeItem('user-tenant-id');
      await apiFetch("/api/auth/logout", { 
        method: "POST", 
        credentials: "include",
        headers: getHeaders()
      });
    },

    updateMyUserData: async (patch) => {
      const tenantId = getEffectiveTenantId();
      const userId = localStorage.getItem('user-id');
      const body = {
        ...(patch && typeof patch === 'object' ? patch : {}),
        ...(userId ? { user_id: userId } : {}),
        ...(tenantId ? { tenant_id: tenantId } : {}),
      };
      const r = await apiFetch("/api/user/me", {
        method: "PATCH",
        headers: getHeaders({ "Content-Type": "application/json" }),
        credentials: "include",
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const err = new Error(`Update failed (${r.status})`);
        err.status = r.status;
        throw err;
      }
      return r.json();
    },

    loginWithRedirect: async (redirectUrl) => {
      const API_BASE_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
        ? 'https://api.neogtm.com'
        : 'https://api.neogtm.com';
      
      const loginUrl = `${API_BASE_URL}/api/auth/login?redirect=${encodeURIComponent(redirectUrl)}`;
      window.location.href = loginUrl;
    },
  },
};